import { Box, Flex, FlexProps } from "@chakra-ui/layout";
import { Collapse, Text } from "@chakra-ui/react";
import { TipoDestinoEnum } from "core/features/documentos/enums";
import useSaveMensagem from "core/features/mensagens/hooks/useSaveMensagem";
import { AssuntoMensagem, MensagemForm } from "core/features/mensagens/typings";
import colors from "core/resources/theme/colors";
import { showSuccess } from "infra/helpers/alerta";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { AiOutlineClose, AiOutlineMinus } from "react-icons/ai";
import { FiMaximize2, FiMinimize2 } from "react-icons/fi";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import ButtonAnexarArquivos from "ui/components/Button/ButtonAnexarArquivos";
import FormNovaMensagem from "./FormNovaMensagem";
import Anexos from "ui/components/Anexos";
import { showConfirm } from "../../../infra/helpers/alerta";

interface DrawerNovaMensagemProps {
  onClose: () => any;
  onSuccess?: () => any;
  defaultValues?: Partial<MensagemForm>;
  compact?: boolean;
}

const IconWrapper = ({
  children,
  ...rest
}: { children: React.ReactNode } & FlexProps) => (
  <Flex
    cursor="pointer"
    rounded="full"
    borderWidth={2}
    borderColor={colors.white}
    w="24px"
    h="24px"
    alignItems="center"
    justifyContent="center"
    {...rest}
  >
    {children}
  </Flex>
);

export type WindowState = "minimized" | "maximized" | "drawer";

const transition: Partial<FlexProps> = {
  transition: "all",
  transitionDuration: "0.25s",
  transitionTimingFunction: "ease-in-out",
};

function DrawerNovaMensagem({
  onClose,
  onSuccess,
  defaultValues,
  compact = false,
}: DrawerNovaMensagemProps) {
  const [previousState, setPreviousState] = useState<WindowState>("maximized");
  const [windowState, setWindowState] = useState<WindowState>("maximized");

  const { control, watch, setValue, handleSubmit, getValues } =
    useForm<MensagemForm>({
      mode: "all",
      defaultValues: {
        tipoDestinoId: TipoDestinoEnum.UmaUnidade,
        assuntoId: AssuntoMensagem.Outros,
        destinos: [],
        tenantId: undefined,
        conteudo: "",
        conteudoPlain: "",
        ...defaultValues,
      },
    });

  const { isLoading, mutate: handleSave } = useSaveMensagem();

  const _isLoading = isLoading;
  const form = watch();

  const MaximizeIcon = windowState === "maximized" ? FiMinimize2 : FiMaximize2;

  const onMinimize = () => {
    const prevState = previousState;
    setWindowState((state) => {
      if (state !== "minimized") {
        setPreviousState(state);
        return "minimized";
      }
      return prevState;
    });
  };

  const onMaximize = () => {
    setWindowState((state) => {
      if (state === "maximized") {
        setPreviousState("drawer");
        return "drawer";
      }
      setPreviousState("maximized");
      return "maximized";
    });
  };

  const _handleSave = async (values: MensagemForm) => {
    let shouldSend = true;

    if (
      values.destinoId === TipoDestinoEnum.TodasUnidades ||
      values.tipoDestinoId === TipoDestinoEnum.TodasUnidades
    ) {
      const { isConfirmed } = await showConfirm(
        "Deseja realmente enviar para todas as unidades?"
      );

      shouldSend = isConfirmed;
    }

    if (!shouldSend) {
      return;
    }

    const _onSuccess = () => {
      showSuccess().then(({ isConfirmed }) => {
        if (isConfirmed) {
          onSuccess?.();
        }
      });
    };

    handleSave(values, {
      onSuccess: _onSuccess,
    });
  };

  return (
    <>
      {windowState === "maximized" ? <Overlay onClick={onMinimize} /> : null}
      <Flex
        position="fixed"
        top={windowState === "maximized" ? "50%" : undefined}
        left={windowState === "maximized" ? "50%" : undefined}
        bottom={windowState === "maximized" ? undefined : 0}
        right={windowState === "maximized" ? undefined : "150px"}
        transform={
          windowState === "maximized" ? "translate(-50%, -50%)" : undefined
        }
        rounded={8}
        shadow="lg"
        flexDir="column"
        zIndex={9999}
        {...transition}
      >
        <Flex
          bg={colors.primary}
          roundedTop={8}
          p={4}
          w="100vw"
          maxW={windowState === "maximized" ? "1024px" : "700px"}
          {...transition}
        >
          <Text color={colors.white} fontSize={16} fontWeight="medium" mr={16}>
            Nova mensagem
          </Text>

          <IconWrapper
            ml="auto"
            onClick={onMinimize}
            alignItems={windowState === "minimized" ? "flex-start" : "flex-end"}
          >
            <AiOutlineMinus color={colors.white} fontSize={16} />
          </IconWrapper>
          <IconWrapper ml={2} onClick={onMaximize}>
            <MaximizeIcon color={colors.white} fontSize={16} />
          </IconWrapper>
          <IconWrapper ml={2} onClick={onClose}>
            <AiOutlineClose color={colors.white} fontSize={16} />
          </IconWrapper>
        </Flex>
        <Collapse in={windowState !== "minimized"}>
          <Flex
            w="100vw"
            maxW={windowState === "maximized" ? "1024px" : "700px"}
            h="75vh"
            maxH={
              windowState === "maximized"
                ? compact
                  ? "400px"
                  : "768px"
                : compact
                ? "400px"
                : "500px"
            }
            flexDir="column"
            bg={colors.white}
            overflow="auto"
            {...transition}
          >
            <Flex flexDir="column" p={4} gridGap={2}>
              <FormNovaMensagem
                windowState={windowState}
                control={control}
                form={form}
                setValue={setValue}
                getValues={getValues}
                compact={compact}
              />
            </Flex>

            <Flex
              gap="16px"
              mt="auto"
              h="fit-content"
              px={4}
              py={2}
              flexDir="column"
            >
              {form.anexos?.length ? (
                <Anexos
                  setValue={(anexos) => setValue("anexos", anexos as File[])}
                  anexos={form.anexos}
                />
              ) : null}
            </Flex>
          </Flex>
          <Flex gap="16px" p={4} roundedBottom={8} mb={4} bg={colors.white}>
            <Flex flex={1}>
              <ButtonAnexarArquivos
                variant="ghost"
                onAnexar={(files) => {
                  if (files) {
                    const currentAnexos = form.anexos;

                    setValue(
                      "anexos",
                      currentAnexos ? [...currentAnexos, ...files] : files
                    );
                  }
                }}
              />
            </Flex>
            <OutlineButton onClick={() => onClose()} isDisabled={_isLoading}>
              Cancelar
            </OutlineButton>
            <PrimaryButton
              onClick={handleSubmit(_handleSave)}
              isLoading={_isLoading}
              disabled={_isLoading}
            >
              Enviar mensagem
            </PrimaryButton>
          </Flex>
        </Collapse>
      </Flex>
    </>
  );
}

const Overlay = ({ onClick }: { onClick?: () => any }) => (
  <Box
    w="100vw"
    h="100vh"
    zIndex={9998}
    bg="#00000045"
    position="fixed"
    top={0}
    left={0}
    onClick={onClick}
  />
);

export default DrawerNovaMensagem;
